import { PagedEntityList, PayrollCalculationsInputResponse, PayrollCalculationsResponse, PayrollEntryEmployeeResponse, PayrollEntryResponse, PayrollListItem, PayrollResponse, PayrollRotation, PayrollTypeModel, PayrollEntryDTO, PayrollEntryManualRequest, IStatementOfEarnings, ProgramStorePair, PayFrequency, PayWeekRM, PayrollAnalysis, PayrollAnalysisSummary, ModuleEnum, PayrollIcdCorporation } from '@herbjoy/apex-api-models';
import { IHttpService, IPromise, IQService } from 'angular';
import moment from 'moment';

export const PayrollTypeEnum = {
    AdvanceWage: 1,
    CommissionAboveThreshold: 2,
    CommissionExceedingGuaranteed: 3,
    PieceBased: 4
};

type PayrollEntryCalculationsVM = PayrollEntryResponse & { Employee: PayrollEntryEmployeeResponse };

type PayrollCalculationsVM = Omit<PayrollCalculationsResponse, "PayrollEntries"> & {
    PayrollEntries: PayrollEntryCalculationsVM[];
};

const apiHeaders = {
    Accept: 'application/json'
};

export class PayrollService {
    constructor(private $http: IHttpService, private $q: IQService, private apexApiUrl: string, private utilityService) {
        'ngInject'
    }

    retrieveTimeEntryException(payrollID) {
        return this.$http.get(`${this.apexApiUrl}/payrolls/${payrollID}/validate`)
            .then(function (response) {
                return response.data;
            });
    }
    /**
     * Retrieve the list of all payroll types in the system.
     * @returns A promise that resolves to the payroll types list.
     */
    retrievePayrollTypes(): IPromise<PayrollTypeModel[]> {
        return this.$http<PayrollTypeModel[]>({
            method: "GET",
            url: this.apexApiUrl + "payroll-types/",
            headers: apiHeaders
        })
            .then(function (response) {
                return response.data;
            });
    }

    /**
     * Retrieve all payrolls that the user can access.
     * @returns A promise the resolves to the payroll list.
     */
    retrievePayrolls(query): IPromise<PayrollListItem[]> {
        return this.$http<PayrollListItem[]>({
            method: "GET",
            url: `${this.apexApiUrl}/payrolls`,
            params: query
        })
            .then(function (response) {
                return response.data;
            });
    }

    /**
     * Retrieve a page of payroll results based on the given query filters
     * and pagination settings.
     * @param {any} query A query used to filter results.
     * @param {number} pageIndex The initial page index to show in results.
     * @param {number} pageSize The page size used to page results.
     */
    retrievePayrollsPage(query, pageIndex, pageSize): IPromise<PagedEntityList<PayrollListItem>> {
        return this.$http<PagedEntityList<PayrollListItem>>({
            method: "GET",
            headers: apiHeaders,
            url: `${this.apexApiUrl}/payrolls/page/${pageIndex}`,
            params: {
                ...query,
                pageSize
            }
        }).then(response => response.data)
    }

    /**
     * Retrieve all payrolls that the processor can access.
     * @returns A promise the resolves to the payroll list.
    */
    retrievePayrollsForProcessors() {
        return this.$http.get(this.apexApiUrl + "payrolls/processing")
            .then(function (response) {
                return response.data;
            });
    }

    /**
     * Retrieve all payrolls that the processor can access.
     * @param {any} query The queries used to filter results
     * @param {number} pageIndex The page index used to get results.
     * @param {number} pageSize The maximum number of results to return.
     * @returns A promise the resolves to the payroll list.
     */
    retrievePayrollsPageForProcessors(query, pageIndex, pageSize): IPromise<PagedEntityList<PayrollListItem>> {
        return this.$http<PagedEntityList<PayrollListItem>>({
            method: "GET",
            url: `${this.apexApiUrl}/payrolls/processing/page/${pageIndex}`,
            params: {
                ...query,
                pageSize
            }
        }).then(response => response.data)
    }

    /**
     * Retrieve all payrolls as an export that the processor can access.
     * @param {any} query The queries used to filter results
     * @returns A promise the resolves to the payroll list.
     */
    retrievePayrollsPageSubmissionsExport(query): IPromise<PagedEntityList<PayrollListItem>> {
        //return this.$http<PagedEntityList<PayrollListItem>>({
        //    method: "GET",
        //    url: `${this.apexApiUrl}/payrolls/processing.xlsx`,
        //    params: {
        //        ...query
        //    }
        //}).then(response => response.data)
        window.location.href = this.apexApiUrl + "/payrolls/processing.xlsx" + this.utilityService.queryStringify(query)
        return null;
    }

    /**
     * Retrieve all payrolls that the user can access belonging to the given corporation
     * @param icdCorporationID {Number} The corporation ID of the company payroll list that will be retrieved.
     * @returns A promise that resolve to the payroll list.
     */
    retrieveIcdCorporationPayrolls(icdCorporationID): IPromise<PayrollListItem[]> {
        if (!icdCorporationID) {
            // Show every week.
            return this.$q.resolve([]);
        }

        return this.$http<PayrollListItem[]>({
            method: "GET",
            url: this.apexApiUrl + "payrolls/icd-corporation/" + icdCorporationID,
            headers: apiHeaders
        })
            .then(function (response) {
                return response.data;
            });
    }

    /**
     * Retrieve the fully computed payroll for the authenticated user's company for the given pay week ID.
     * @param payrollID the payroll id of the payroll to retrieve.
     * @returns A promise that resolves to the requested payroll.
     */
    retrievePayroll(payrollID): IPromise<PayrollResponse> {
        if (!payrollID) {
            return this.$q.when(null);
        }
        return this.$http<PayrollResponse>({
            method: "GET",
            url: this.apexApiUrl + "payrolls/" + payrollID,
            headers: apiHeaders
        })
            .then(function (response) {
                return response.data;
            });
    }

    retrievePayrollCalculationsInput(payrollID) {
        return this.$http<PayrollCalculationsInputResponse>({
            method: "GET",
            url: `${this.apexApiUrl}/payrolls/${payrollID}/calculations/input`,
        })
            .then(response => response.data);
    }

    calculatePayrollTotals(payrollID, payrollInput) {
        // Remove local time component from any date objects.
        const data = {
            ...payrollInput,
            StartDate: moment(payrollInput.StartDate).format("YYYY-MM-DD"),
            EndDate: moment(payrollInput.EndDate).format("YYYY-MM-DD"),
            PayPeriods: payrollInput.PayPeriods.map(p => ({
                ...p,
                StartDate: moment(p.StartDate).format("YYYY-MM-DD"),
                EndDate: moment(p.EndDate).format("YYYY-MM-DD"),
                ManualSubmission: !p.ManualSubmission ? null : {
                    ...p.ManualSubmission,
                    ExtraShowHours: p.ManualSubmission.ExtraShowHours.map(q => ({
                        ...q,
                        Date: moment(q.Date).format("YYYY-MM-DD")
                    }))
                }
            })),
            PayDateDurations: payrollInput.PayDateDurations.map(p => ({
                ...p,
                Date: moment(p.Date).format("YYYY-MM-DD")
            })),
            RSCommissionAmounts: payrollInput.RSCommissionAmounts.map(p => ({
                ...p,
                ScheduleDate: moment(p.ScheduleDate).format("YYYY-MM-DD")
            }))
        };
        return this.$http<PayrollCalculationsResponse>({
            method: "POST",
            url: `${this.apexApiUrl}/payrolls/${payrollID}/calculations`,
            data
        })
            .then(response => response.data)
            .then((calculations: PayrollCalculationsVM) => {
                // Denormalize the relational data.
                for (const entry of calculations.PayrollEntries) {
                    entry.Employee = calculations.Employees.find(p => p.EmployeeID === entry.EmployeeID);
                }
                return calculations;
            });
    }

    /**
     * Get the list of pay week rotations.
     * @param query
     */
    retrieveRotations(query: { icdCorporationID: number }) {
        return this.$http<PayrollRotation[]>({
            method: "GET",
            url: this.apexApiUrl + "rotations",
            params: query,
            headers: apiHeaders
        })
            .then(function (response) {
                return response.data;
            });
    }

    /**
     * Save changes for the given payroll entry belonging to the given payroll.
     * @param payrollID The id of the payroll.
     * @param payrollEntry The modified payroll entry.
     * @returns A promise that resolves when the update is complete.
     */
    savePayrollEntry(payrollID: number, payrollEntry: PayrollEntryDTO) {
        const dto = mapPayrollEntryToDTO(payrollEntry);
        return this.$http({
            method: "PUT",
            url: this.apexApiUrl + "payrolls/" + payrollID + "/entries/" + payrollEntry.PayPeriodID,
            headers: {
                "Content-Type": "application/json"
            },
            data: dto
        });
    }

    /**
     * Save changes to the given payroll's memo field.
     * @param payrollID
     * @param memo The new text to put in the memo field.
     * @returns A promise that resolves when the update is complete.
     */
    savePayrollMemo(payrollID: number, memo: string) {
        return this.$http({
            method: "PUT",
            url: this.apexApiUrl + "payrolls/" + payrollID + "/memo",
            headers: {
                "Content-Type": "application/json"
            },
            data: JSON.stringify(memo)
        });
    }

    savePayrollManualRequests(payrollID: number, manualRequests: PayrollEntryManualRequest[]) {
        const data = manualRequests.map(function (request) {
            return {
                PayPeriodID: request.PayPeriodID,
                IsProcessedByAdp: request.IsProcessedByAdp
            };
        });

        return this.$http({
            method: "PATCH",
            url: this.apexApiUrl + "payrolls/" + payrollID + "/manual-requests",
            headers: {
                "Content-Type": "application/json"
            },
            data: data
        });
    }

    /**
     * Lock the payroll with the given ID.
     * @param payrollID
     * @returns A promise that resolves when the update is complete.
     */
    submitPayroll(payrollID: number) {
        return this.savePayrollLocked(payrollID, true);
    }

    /**
     * Unlock the payroll with the given ID.
     * @param payrollID
     * @returns A promise that resolves when the update is complete.
     */
    unlockPayroll(payrollID: number) {
        return this.savePayrollLocked(payrollID, false);
    }

    savePayrollVisible(payrollID: number, visible: boolean) {
        return this.$http({
            method: "PUT",
            url: this.apexApiUrl + "payrolls/" + payrollID + "/visible/" + visible,
            headers: {
                "Content-Type": "application/json"
            },
            data: "{}"
        });
    }

    /**
     * Update whether the payroll with the given ID is locked.
     * @param payrollID
     * @param locked {Boolean} The requested submitted or submitted state for the payroll.
     * @returns A promise that resolves when the update is complete.
     */
    savePayrollLocked(payrollID: number, locked: boolean) {
        return this.$http({
            method: "PUT",
            url: this.apexApiUrl + "payrolls/" + payrollID + "/locked/" + locked,
            headers: {
                "Content-Type": "application/json"
            },
            data: "{}"
        });
    }

    /**
     * Update whether the payroll with the given ID is disclosed to employees.
     * @param payrollID
     * @param disclosed {Boolean} Whether or not the payroll should be updated to be disclosed.
     * @returns A promise that resolves when the update is complete.
     */
    savePayrollDisclosed(payrollID: number, disclosed: boolean) {
        return this.$http({
            method: "PUT",
            url: this.apexApiUrl + "payrolls/" + payrollID + "/disclosed/" + disclosed,
            headers: {
                "Content-Type": "application/json"
            },
            data: "{}"
        });
    }

    /**
     * Assign the given employee as a payroll admin for the given payroll.
     * @returns A promise that resolves when the update is complete.
     */
    assignPayrollAdmin(payrollID: number, employeeID: number) {
        return this.$http({
            method: "POST",
            url: this.apexApiUrl + "payrolls/" + payrollID + "/admins",
            headers: {
                "Content-Type": "application/json"
            },
            data: JSON.stringify(employeeID)
        });
    }

    /**
     * Unassign the given employee from being the payroll admin for the given payroll.
     * @returns A promise that resolve when the update is complete.
     */
    unassignPayrollAdmin(payrollID: number, employeeID: number) {
        return this.$http({
            method: "DELETE",
            url: this.apexApiUrl + "payrolls/" + payrollID + "/admins/" + employeeID,
            // Yet another hack to get around weird behaviour in Chrome (it fails the CORS preflight otherwise):
            headers: {
                "Content-Type": "application/json"
            },
            data: {}
        });
    }

    /**
     * Update whether the payroll with the given ID is in a pending state where external
     * acknowledgments need to be made before work can continue.
     * @param payrollID
     * @param pending Whether or not the payroll should be updated to be pending.
     * @returns A promise that resolves when the update is complete.
     */
    savePayrollPending(payrollID: number, pending: boolean) {
        return this.$http({
            method: "PUT",
            url: this.apexApiUrl + "payrolls/" + payrollID + "/pending/" + pending,
            headers: {
                "Content-Type": "application/json"
            },
            data: "{}"
        });
    }

    /**
     * Update whether the payroll with the given ID has been processed internally and should
     * now be considered complete.
     * @param payrollID
     * @param completed Whether or not the payroll should be updated to be completed.
     * @returns A promise that resolves when the update is complete.
     */
    savePayrollCompleted(payrollID: number, completed: boolean) {
        return this.$http({
            method: "PUT",
            url: this.apexApiUrl + "payrolls/" + payrollID + "/completed/" + completed,
            headers: {
                "Content-Type": "application/json"
            },
            data: "{}"
        });
    }

    /**
     * Dismiss the notification that the given payroll is still pending.
     * @param {number} payrollID The payroll for which the notification will be dismissed.
     */
    dismissPayrollPendingMessage(payrollID: number) {
        return this.$http.post(this.apexApiUrl + "payrolls/pending/" + payrollID + "/dismissals", {});
    }

    /**
     * Dismiss the notification that the given payroll is submitted.
     * @param {number} payrollID The payroll for which the notification will be dismissed
     */
    dismissPayrollSubmittedMessage(payrollID: number) {
        return this.$http.post(this.apexApiUrl + "payrolls/submitted/" + payrollID + "/dismissals", {});
    }

    /**
     * Retrieve the statement of earnings containing the commissions data and daily breakdown
     * for the given pay week, program, and store.
     */
    retrieveStatementOfEarnings(payrollID, programID, storeID, filteredEmployeeID) {
        return this.$http<IStatementOfEarnings>({
            method: "GET",
            url: this.apexApiUrl + "payroll/" + payrollID + "/statement-of-earnings"
                + "?programID=" + programID + "&storeID=" + storeID + "&filteredEmployeeID=" + filteredEmployeeID,
            headers: apiHeaders
        })
            .then(function (response) {
                return response.data;
            });
    }

    /**
     * Retrieve the collection of programs and stores that have time entered or commissionable
     * sales for the given pay week as a pair.
     */
    retrieveActiveProgramStorePairs(payrollID, filteredEmployeeID) {
        return this.$http<ProgramStorePair>({
            method: "GET",
            url: this.apexApiUrl + "payroll/" + payrollID + "/active-program-store-pairs"
                + (filteredEmployeeID ? "?filteredEmployeeID=" + filteredEmployeeID : ""),
            headers: apiHeaders
        })
            .then(function (response) {
                return response.data;
            });
    }

    retrievePayFrequencies() {
        return this.$http.get<PayFrequency>(this.apexApiUrl + "pay-frequencies")
            .then(function (response) {
                return response.data;
            });
    }

    /**
     * @deprecated
     */
    retrievePayWeeks() {
        return this.$http.get<PayWeekRM>(this.apexApiUrl + "payroll/payweeks")
            .then(response => response.data);
    }

    retrievePayrollAnalysisSummary(startDate, endDate, programID, retailerID, owner) {
        const params: any = {};
        if (startDate) {
            params.startDate = moment(startDate).format("YYYY-MM-DD");
        }
        if (endDate) {
            params.endDate = moment(endDate).format("YYYY-MM-DD");
        }
        if (programID) {
            params.programID = programID;
        }
        if (retailerID) {
            params.retailerID = retailerID;
        }
        if (owner) {
            params.owner = owner;
        }

        return this.$http<PayrollAnalysisSummary[]>({
            method: "GET",
            url: this.apexApiUrl + "payrolls/summary",
            params: params
        })
            .then(function (response) {
                return response.data;
            })
    }

    retrievePayrollAnalysis(payrollID, programID, retailerID, storeID) {
        const params: any = {};
        if (programID) {
            params.programID = programID;
        }
        if (retailerID) {
            params.retailerID = retailerID;
        }
        if (storeID) {
            params.storeID = storeID;
        }

        return this.$http<PayrollAnalysis[]>({
            method: "GET",
            url: this.apexApiUrl + "payrolls/" + payrollID + "/analysis",
            params: params
        })
            .then(function (response) {
                return response.data;
            })
    }

    /**
     * Get the list of pay periods where the given employee appeared on the payroll.
     * @param employeeID The employee whose pay periods will be returned
     * @param module Include employees within a specific module, if the user is
     * a module admin.
     * @param locked Filter by whether the payroll is locked or not
     * @param time If true, only include payrolls where the employee entered time.
     */
    retrievePayPeriods(employeeID: number, module: ModuleEnum, locked?: boolean, time?: boolean) {
        return this.$http<PayrollListItem[]>({
            method: "GET",
            url: this.apexApiUrl + "pay-periods/employee/" + employeeID,
            params: {
                module,
                locked,
                time
            }
        })
            .then(function (response) {
                return response.data;
            });
    }

    /**
     * Retrive all corporation having active payroll
     */
    retrieveAllPayrollCorporations() {

        return this.$http<PayrollIcdCorporation[]>({
            method: "GET",
            url: this.apexApiUrl + "payrolls/icd-corporations",
            headers: { Accept: "application/json" }
        }).then(function (response) {
            return response.data;
        });
    }

}


/**
 * Create a serializable DTO from the client-side payroll entry object
 * that only contains the fields that can be updated through the API.
 * @returns The DTO containing the payroll entry's attributes.
 */
function mapPayrollEntryToDTO(payrollEntry) {
    return {
        PayPeriodID: payrollEntry.PayPeriodID,
        IsPaidGuaranteed: payrollEntry.IsPaidGuaranteed,
        IsPaidCommission: payrollEntry.IsPaidCommission,
        ExtraHoursWorked: payrollEntry.ExtraHoursWorked,
        ManualCommissionAmount: payrollEntry.ManualCommissionAmount,
        Salary: payrollEntry.Salary === payrollEntry.DefaultSalary ? null : payrollEntry.Salary,
        CommissionThreshold: payrollEntry.CommissionThreshold,
        ContractorPayment: payrollEntry.ContractorPayment === payrollEntry.DefaultContractorPayment
            ? null : payrollEntry.ContractorPayment,
        Bonus: payrollEntry.Bonus,
        CommissionBonus: payrollEntry.CommissionBonus,
        VolumeBonus: payrollEntry.VolumeBonus,
        TravelAllowance: payrollEntry.TravelAllowance,
        CellAllowance: payrollEntry.CellAllowance,
        Tip: payrollEntry.Tip,
        SickPay: payrollEntry.SickPay,
        SplitTime: payrollEntry.SplitTime,
        ReportingTime: payrollEntry.ReportingTime,
        MealPeriodPremiumTime: payrollEntry.MealPeriodPremiumTime,
        HolidayPay: payrollEntry.HolidayPay,
        Chargeback: payrollEntry.Chargeback,
        TrainingPayHours: payrollEntry.TrainingPayHours,
        NetPayAddition: payrollEntry.NetPayAddition,
        NetPayDeduction: payrollEntry.NetPayDeduction,
        Comments: payrollEntry.Comments,
        CommissionPieces: payrollEntry.CommissionPieces
    };
}
